import React from 'react';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { PasswordResetCard } from './PasswordResetCard';
import { TourReplayCard } from './TourReplayCard';

interface AccountSectionProps {
  onClose: () => void;
}

export const AccountSection: React.FC<AccountSectionProps> = ({ onClose }) => {
  const { user, logout } = useAuth();

  const handleSignOut = () => {
    onClose();
    logout();
  };

  return (
    <div className="space-y-6">
      <Card padding="none" className="overflow-hidden">
        <div className="relative px-5 py-6 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 backdrop-blur-sm rounded-2xl flex items-center justify-center text-[20px] font-semibold ring-2 ring-white/20 bg-white/10 text-white">
              {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-[16px] font-semibold text-white">
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'}
              </p>
              <p className="text-[13px] text-white/60">{user?.email || ''}</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-medium text-neutral-400 mb-1.5">
                First Name
              </label>
              <div className="px-3.5 py-2.5 text-[13px] rounded-xl font-medium bg-neutral-50 text-neutral-900">
                {user?.firstName || '—'}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-neutral-400 mb-1.5">
                Last Name
              </label>
              <div className="px-3.5 py-2.5 text-[13px] rounded-xl font-medium bg-neutral-50 text-neutral-900">
                {user?.lastName || '—'}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div>
        <h3 className="text-[13px] font-semibold text-neutral-900 mb-3">Security</h3>
        <PasswordResetCard />
      </div>

      <div>
        <h3 className="text-[13px] font-semibold text-neutral-900 mb-3">Getting Started</h3>
        <TourReplayCard onClose={onClose} />
      </div>

      <div className="pt-4 border-t border-neutral-100">
        <Button
          id="sign-out-button"
          variant="ghost"
          onClick={handleSignOut}
          leftIcon={<LogOut className="w-4 h-4" />}
          aria-label="Sign out of your account"
        >
          Sign out
        </Button>
      </div>
    </div>
  );
};
